import { useCallback, useEffect, useRef, useState } from "react";
import { getCustomerProfile, requestCustomerPin, verifyCustomerPin, updateCustomerProfile, getCustomerSongs } from "../../services/customer";
import { errorCode } from "../../untils/constanst";

const initialState = { step: "phone", mobile: "", notice: "", error: "", loading: false, profile: null };
const responseError = (response) => {
  if (response?.code !== 2000) {
    const error = new Error(response?.message || errorCode[response?.code] || "Chưa thể xử lý yêu cầu. Vui lòng thử lại.");
    error.code = response?.code;
    throw error;
  }
  return response;
};
export default function useCustomerSession(open) {
  const [state, setState] = useState(initialState);
  const session = useRef({ token: "", expires: 0 });
  const request = useRef({ id: 0, controller: null });
  const reset = useCallback((notice = "") => {
    request.current.controller?.abort();
    request.current.id += 1;
    request.current.saving = false;
    session.current = { token: "", expires: 0 };
    setState({ ...initialState, notice });
  }, []);
  useEffect(() => {
    if (!open) reset();
    return () => {
      request.current.controller?.abort();
      request.current.id += 1;
      session.current = { token: "", expires: 0 };
    };
  }, [open, reset]);
  useEffect(() => {
    if (!session.current.expires) return undefined;
    const timer = setTimeout(() => reset("Phiên tra cứu đã hết hạn. Vui lòng xác thực lại."), Math.max(0, session.current.expires - Date.now()));
    return () => clearTimeout(timer);
  }, [state.step, reset]);
  const run = async (operation) => {
    request.current.controller?.abort();
    const controller = new AbortController();
    const id = request.current.id + 1;
    request.current = { id, controller, saving: request.current.saving };
    const active = () => request.current.id === id && !controller.signal.aborted;
    const update = (value) => { if (active()) setState((previous) => ({ ...previous, ...value })); };
    update({ loading: true, error: "", notice: "" });
    try { return await operation(controller.signal, update, active); }
    catch (error) {
      if (!active()) return;
      if (error.response?.status === 401 || error.code === 1002) reset("Phiên tra cứu đã hết hạn. Vui lòng xác thực lại.");
      else update({ error: error.response?.data?.message || errorCode[error.response?.data?.code] || (error.isAxiosError ? "Không thể kết nối. Vui lòng thử lại." : error.message) });
    } finally { if (active()) request.current.saving = false; update({ loading: false }); }
  };
  const loadProfile = async (signal, update) => {
    const response = responseError(await getCustomerProfile(session.current.token, signal));
    if (!response.data || typeof response.data !== "object" || Array.isArray(response.data)) throw new Error("Chưa tải được thông tin khách hàng. Vui lòng thử lại.");
    update({ profile: response.data, step: "profile" });
  };
  const requestPin = (mobile) => run(async (signal, update) => {
    const response = responseError(await requestCustomerPin({ mobile }, signal));
    update({ mobile, step: "pin", notice: response.pin_exists ? "Số điện thoại này đã có mã PIN. Vui lòng nhập PIN để xem thông tin." : "Mã PIN đã được gửi qua Zalo ZNS." });
  });
  const verifyPin = (pin) => run(async (signal, update, active) => {
    const response = responseError(await verifyCustomerPin({ mobile: state.mobile, pin }, signal));
    if (!active()) return;
    if (!response.token) throw new Error("Chưa xác thực được phiên tra cứu. Vui lòng thử lại.");
    let expires = Date.now() + 60 * 60 * 1000;
    try {
      const payload = JSON.parse(atob(response.token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      if (Number.isFinite(payload.exp)) expires = payload.exp * 1000;
    } catch { /* The server remains the authority for token validity. */ }
    session.current = { token: response.token, expires };
    update({ step: "loadingProfile" });
    await loadProfile(signal, update);
  });
  const saveProfile = (fields) => {
    if (state.loading || request.current.saving || !session.current.token) return Promise.resolve(false);
    request.current.saving = true;
    return run(async (signal, update, active) => {
      const response = responseError(await updateCustomerProfile(session.current.token, fields, signal));
      if (!active()) return false;
      if (!response.data?.customer) throw new Error("Chưa nhận được thông tin đã lưu. Vui lòng tải lại hồ sơ.");
      update({ profile: { ...state.profile, customer: { ...state.profile.customer, ...response.data.customer } }, notice: "Đã lưu thông tin của bạn." });
      return true;
    });
  };
  const searchSongs = useCallback(async (params, signal) => {
    const currentSession = session.current;
    if (!currentSession.token) throw new Error("Vui lòng xác thực lại.");
    try {
      const response = responseError(await getCustomerSongs(currentSession.token, params, signal));
      if (session.current !== currentSession || signal?.aborted) throw new Error("Yêu cầu đã hủy.");
      if (!Array.isArray(response.data?.items) || !Number.isInteger(response.data?.total) || response.data.total < 0) {
        throw new Error("Dữ liệu bài hát không hợp lệ. Vui lòng thử lại.");
      }
      return response.data;
    } catch (error) {
      if (session.current === currentSession && !signal?.aborted && (error.response?.status === 401 || error.code === 1002)) {
        reset("Phiên tra cứu đã hết hạn. Vui lòng xác thực lại.");
      }
      throw error;
    }
  }, [reset]);
  return { ...state, reset, requestPin, verifyPin, saveProfile, searchSongs, retryProfile: () => run(loadProfile) };
}
